import GenericFeedbackWidget from 'components/surveys/generic-feedback-widget/GenericFeedbackWidget.tsx'
import { SalesFeedbackWidgetStep } from './types.ts'
import {
  SALES_FEEDBACK_CONFIG,
  SALES_FEEDBACK_WIDGET_STEPS_ORDER,
  SALES_SURVEY_FEEDBACK_WIDGET_ID
} from 'components/surveys/sales-survey/constants.ts'
import { GenericFeedbackWidgetProps } from 'components/surveys/generic-feedback-widget/types.ts'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { selectShowSalesSurvey, setShowSalesSurvey } from 'store/app/slice.ts'
import { UserTypes } from 'store/auth/types.ts'
import { getAxiosRequestConfig, newRequest } from 'utils/request.ts'
import { Methods } from 'types/request.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { mapWidgetInputsToSurveyPayload } from 'components/surveys/generic-feedback-widget/utils.ts'
import { BoltSurveyType } from 'components/surveys/types.ts'
import { useSendSurveyClosedEventMutation } from 'store/queries/bolt-api/contacts'

const FeedbackWidgetSalesSurvey = () => {
  // Redux
  const showSalesSurvey = useStoreSelector(selectShowSalesSurvey)
  const { userType } = useStoreSelector((store) => store.auth)
  const { salesSurvey } = useStoreSelector((store) => store.app)
  const { billingContracts, contact } = useStoreSelector((store) => store.contact)
  const salesSurveyBillingContract = salesSurvey.contractId ? billingContracts[salesSurvey.contractId] : null
  const dispatch = useStoreDispatch()

  const [sendSurveyClosedEvent] = useSendSurveyClosedEventMutation()

  /**
   * Handles the form submission for the sales survey feedback widget
   * @param {FeedbackWidgetInputs<SalesFeedbackWidgetStep>} data
   * @returns {Promise<{success: boolean}>}
   */
  const submitSalesSurvey: GenericFeedbackWidgetProps<SalesFeedbackWidgetStep>['onSubmit'] = async (data) => {
    try {
      if (!contact?.sfId) throw new Error('No contact given')
      if (!salesSurveyBillingContract) throw new Error('No sales survey billing contract found')

      const body = mapWidgetInputsToSurveyPayload(
        data,
        SALES_FEEDBACK_WIDGET_STEPS_ORDER,
        SALES_FEEDBACK_CONFIG,
        BoltSurveyType.PUSH_SURVEY
      )

      const { success, message } = await newRequest(
        getAxiosRequestConfig(
          Methods.POST,
          `/contacts/${contact.sfId}/accounts/${salesSurveyBillingContract.accountSfId}/opportunities/${salesSurveyBillingContract.opportunitySfId}/surveys`,
          body
        )
      )

      if (success) {
        return { success: true }
      } else {
        log({
          error: message || 'Failed to submit sales survey',
          identifier: `[${Flow.SURVEY}:sales-survey]`,
          metaData: {
            surveyInputs: data
          }
        })
        return { success: false }
      }
    } catch (error) {
      log({
        error: error instanceof Error ? error : 'Unknown error',
        identifier: `[${Flow.SURVEY}:sales-survey(catch)]`,
        metaData: {
          surveyInputs: data
        }
      })
      return { success: false }
    }
  }

  return (
    <GenericFeedbackWidget<SalesFeedbackWidgetStep>
      id={SALES_SURVEY_FEEDBACK_WIDGET_ID}
      steps={SALES_FEEDBACK_WIDGET_STEPS_ORDER}
      config={SALES_FEEDBACK_CONFIG}
      onSubmit={submitSalesSurvey}
      onClose={() => {
        if (
          contact?.sfId &&
          salesSurveyBillingContract &&
          salesSurveyBillingContract.accountSfId &&
          salesSurveyBillingContract.opportunitySfId
        ) {
          void sendSurveyClosedEvent({
            surveyType: BoltSurveyType.PUSH_SURVEY,
            contactId: contact.sfId,
            accountId: salesSurveyBillingContract.accountSfId,
            opportunityId: salesSurveyBillingContract.opportunitySfId
          })
        }
        dispatch(setShowSalesSurvey({ show: false }))
      }}
      onCloseConfirmation={() => {
        dispatch(setShowSalesSurvey({ show: false, isEligible: false }))

        // Delayed dispatch to ensure the animation is completed before hiding the widget
        setTimeout(() => {
          dispatch(setShowSalesSurvey({ isEligible: false }))
        }, 500)
      }}
      customCanPopUp={userType === UserTypes.CUSTOMER && salesSurvey.isEligible}
      openFeedbackWidget={showSalesSurvey}
      withIntro={salesSurvey.show === 'intro'}
      introAs="modal"
    />
  )
}

export default FeedbackWidgetSalesSurvey
